import React from "react";
import {
  Card,
  Button,
  Tag,
  Spinner,
  NonIdealState,
  Callout,
  Text,
  H4,
  H5,
  ControlGroup,
  Divider,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";
import { DatePicker, TimePicker } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";
import { filterBookingsForSlotMetrics, shouldIncludeBookingInSlotMetrics } from "../../utils/surgerySlotUtils";

class SurgeryCalendar extends React.Component {

  state = {
    // Calendar state
    selectedDate: null,
    isLoadingCalendar: false,
    isLoadingSlots: false,

    // Calendar data
    calendarData: {},
    availableDates: [],
    leaveDates: [], // Doctor leave dates fetched from chrono API
    closureDates: [], // Hospital closures and holidays

    // Slots data
    timeSlots: [],
    selectedSlot: null,

    // Current month view
    currentMonth: new Date(),

    // Booked counts for each date
    bookedCountsByDate: {},
    surgeriesByDate: {}, // Store full surgery data for tooltips

    suppressChange: false,
  };

  // Robust matcher: supports numeric (0-6), strings (Mon/Monday), arrays, and tokenized strings
  dayMatches = (scheduleDay, dateMoment) => {
    if (scheduleDay === null || typeof scheduleDay === "undefined") return false;
    const targetIdx = parseInt(moment(dateMoment).format("d"), 10); // 0..6
    const targetFull = moment(dateMoment).format("dddd").toLowerCase();
    const targetAbbr = moment(dateMoment).format("ddd").toLowerCase();

    const matchToken = (token) => {
      if (token === null || typeof token === "undefined") return false;
      if (typeof token === "number") return token === targetIdx;
      const str = String(token).trim().toLowerCase();
      if (str === "") return false;
      if (!isNaN(parseInt(str, 10))) {
        return parseInt(str, 10) === targetIdx;
      }
      return str === targetFull || str === targetAbbr;
    };

    if (typeof scheduleDay === "number") return scheduleDay === targetIdx;
    if (Array.isArray(scheduleDay)) return scheduleDay.some((d) => this.dayMatches(d, dateMoment));

    // Support comma/space/pipe-separated strings
    const tokens = String(scheduleDay)
      .split(/[\s,|/]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    return tokens.some(matchToken);
  };

  componentDidMount() {
    if (this.props.doctorId && this.props.branchId) {
      this.debouncedLoadCalendarData();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.doctorId !== this.props.doctorId || prevProps.branchId !== this.props.branchId) {
      this.setState({
        selectedDate: null,
        selectedSlot: null,
        calendarData: {},
        timeSlots: [],
        bookedCountsByDate: {},
      });

      if (this.props.doctorId && this.props.branchId) {
        this.debouncedLoadCalendarData();
      }
    }
  }

  // Debounced version to prevent rapid successive calls
  debouncedLoadCalendarData = _.debounce(() => {
    this.loadCalendarData();
  }, 300);

  componentWillUnmount() {
    // Cancel any pending debounced calls to prevent memory leaks
    this.debouncedLoadCalendarData.cancel();
  }

  // Expose a manual refresh hook to parent components
  forceRefresh = () => {
    console.log("📊 SurgeryCalendar: manual force refresh triggered");
    this.loadCalendarData();
  };

  loadCalendarData = async () => {
    console.log("📊 SurgeryCalendar: loadCalendarData invoked", {
      doctorId: this.props.doctorId,
      branchId: this.props.branchId,
      branchName: this.props.branchName,
    });

    if (!this.props.doctorId || !this.props.branchId) {
      this.setState({
        calendarData: {},
        availableDates: [],
        leaveDates: [],
        closureDates: [],
        bookedCountsByDate: {},
        surgeriesByDate: {},
        isLoadingCalendar: false,
      });
      return;
    }

    try {
      this.setState({ isLoadingCalendar: true });

      const startDate = moment(this.state.currentMonth).startOf("month").toDate();
      const endDate = moment(this.state.currentMonth).endOf("month").toDate();
      const startDateStr = moment(startDate).format("YYYY-MM-DD");
      const endDateStr = moment(endDate).format("YYYY-MM-DD");

      let schedulesResponse, leaveDatesResponse, closureDatesResponse;
      if (this.props.authStore?.useUnifiedCalendarApi) {
        const calendarEndpoint = `/v3/${this.props.authStore.prefix}/neoSxCalendar/getDoctorMonth`;
        const unified = await apiCallPost(calendarEndpoint, {
          doctorId: this.props.doctorId,
          branchName: this.props.branchName || this.props.branchId,
          startDate: startDateStr,
          endDate: endDateStr,
        });
        schedulesResponse = unified?.schedules || unified?.data?.schedules || [];
        leaveDatesResponse = unified?.leaves || [];
        closureDatesResponse = unified?.closures || [];
      } else {
        [schedulesResponse, leaveDatesResponse, closureDatesResponse] = await Promise.all([
          apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/getAll`, {
            doctorId: this.props.doctorId,
            branchName: this.props.branchName || this.props.branchId,
            effectiveFrom: startDateStr,
            effectiveTo: endDateStr,
          }),
          this.loadLeaveDates(startDate, endDate),
          this.loadClosureDates(startDate, endDate),
        ]);
      }

      let schedules = Array.isArray(schedulesResponse)
        ? schedulesResponse
        : Array.isArray(schedulesResponse?.data?.items)
        ? schedulesResponse.data.items
        : Array.isArray(schedulesResponse?.data)
        ? schedulesResponse.data
        : Array.isArray(schedulesResponse?.items)
        ? schedulesResponse.items
        : [];

      if (!schedules || schedules.length === 0) {
        try {
          const fallbackRes = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxDoctorScheduleMaster/getAll`, {
            doctorId: this.props.doctorId,
            branchName: this.props.branchName || this.props.branchId,
          });
          schedules = Array.isArray(fallbackRes)
            ? fallbackRes
            : Array.isArray(fallbackRes?.data?.items)
            ? fallbackRes.data.items
            : Array.isArray(fallbackRes?.data)
            ? fallbackRes.data
            : Array.isArray(fallbackRes?.items)
            ? fallbackRes.items
            : [];
        } catch (fallbackError) {
          console.warn("Fallback schedule fetch failed", fallbackError);
        }
      }

      const leaveDates = leaveDatesResponse || [];
      const closureDates = closureDatesResponse || [];
      const availableDates = [];
      const scheduleMap = {};

      const startOfMonth = moment(this.state.currentMonth).startOf("month");
      const endOfMonth = moment(this.state.currentMonth).endOf("month");
      const leaveSet = new Set(leaveDates.map((l) => moment(l.date).format("YYYY-MM-DD")));
      const closureSet = new Set(closureDates.map((c) => moment(c.date).format("YYYY-MM-DD")));

      schedules.forEach((schedule) => {
        const effFrom = schedule.effectiveFrom ? moment(schedule.effectiveFrom) : null;
        const effTo = schedule.effectiveTo ? moment(schedule.effectiveTo) : null;
        const rangeStart = effFrom ? moment.max(effFrom.startOf("day"), startOfMonth) : startOfMonth.clone();
        const rangeEnd = effTo ? moment.min(moment(effTo).endOf("day"), endOfMonth) : endOfMonth.clone();
        if (rangeStart.isAfter(rangeEnd, "day")) return;

        const cursor = rangeStart.clone();
        while (cursor.isSameOrBefore(rangeEnd, "day")) {
          const dayField = schedule.day ?? schedule.days;
          if (this.dayMatches(dayField, cursor)) {
            const dateString = cursor.format("YYYY-MM-DD");
            const isLeaveDate = leaveSet.has(dateString);
            const isClosureDate = closureSet.has(dateString);
            const isPast = cursor.isBefore(moment(), "day");

            if (!scheduleMap[dateString]) {
              scheduleMap[dateString] = {
                available: false,
                isLeave: isLeaveDate,
                isClosure: isClosureDate,
                schedules: [],
                dayName: cursor.format("dddd"),
                isPast,
                leaveData: isLeaveDate ? leaveDates.find((l) => moment(l.date).isSame(cursor, "day")) : undefined,
                closureData: isClosureDate ? closureDates.find((c) => moment(c.date).isSame(cursor, "day")) : undefined,
              };
            }

            if (!isLeaveDate && !isClosureDate) {
              scheduleMap[dateString].schedules.push(schedule);
              scheduleMap[dateString].available = true;
              if (!availableDates.includes(dateString)) availableDates.push(dateString);
            }
          }
          cursor.add(1, "day");
        }
      });

      const cursorFill = startOfMonth.clone();
      while (cursorFill.isSameOrBefore(endOfMonth, "day")) {
        const dateString = cursorFill.format("YYYY-MM-DD");
        if (!scheduleMap[dateString]) {
          const isLeaveDate = leaveSet.has(dateString);
          const isClosureDate = closureSet.has(dateString);
          scheduleMap[dateString] = {
            available: false,
            isLeave: isLeaveDate,
            isClosure: isClosureDate,
            schedules: [],
            dayName: cursorFill.format("dddd"),
            isPast: cursorFill.isBefore(moment(), "day"),
            leaveData: isLeaveDate ? leaveDates.find((l) => moment(l.date).isSame(cursorFill, "day")) : undefined,
            closureData: isClosureDate ? closureDates.find((c) => moment(c.date).isSame(cursorFill, "day")) : undefined,
          };
        }
        cursorFill.add(1, "day");
      }

      const { bookedCountsByDate, surgeriesByDate } = await this.loadBookedCountsForMonth(availableDates, scheduleMap);

      this.setState({
        calendarData: { availableDates, leaveDates, scheduleMap, closureDates },
        availableDates,
        leaveDates,
        closureDates,
        bookedCountsByDate,
        surgeriesByDate,
        isLoadingCalendar: false,
      });
    } catch (error) {
      console.error("Error loading calendar data:", error);
      cogoToast.error("Failed to load calendar availability");
      this.setState({ isLoadingCalendar: false });
    }
  };

  loadLeaveDates = async (startDate, endDate) => {
    if (!this.props.doctorId) {
      return [];
    }

    try {
      console.log(
        "Fetching leave dates for range:",
        moment(startDate).format("YYYY-MM-DD"),
        "to",
        moment(endDate).format("YYYY-MM-DD")
      );

      // Try to get leaves for the entire date range in a single API call
      // If the API doesn't support date ranges, we'll fall back to weekly batches
      const leaveDates = [];

      // Helper: expand range (from/to) into date strings within [startDate, endDate]
      const expandRangeToDates = (fromStr, toStr, rangeMeta = {}) => {
        const results = [];
        if (!fromStr || !toStr) return results;
        const rangeStart = moment(fromStr).startOf("day");
        const rangeEnd = moment(toStr).endOf("day");
        const windowStart = moment(startDate).startOf("day");
        const windowEnd = moment(endDate).endOf("day");

        const start = moment.max(rangeStart, windowStart);
        const end = moment.min(rangeEnd, windowEnd);
        const cursor = start.clone();
        while (cursor.isSameOrBefore(end, "day")) {
          results.push({
            date: cursor.format("YYYY-MM-DD"),
            leaveType: rangeMeta.leaveType || "Leave",
            reason: rangeMeta.reason || "",
            leaveData: {
              from: fromStr,
              to: toStr,
              originalFrom: rangeMeta.originalFrom,
              originalTo: rangeMeta.originalTo,
              leaveType: rangeMeta.leaveType,
              reason: rangeMeta.reason,
            },
          });
          cursor.add(1, "day");
        }
        return results;
      };

      // First, try a single API call with date range (if supported by the API)
      try {
        const resLeaves = await apiCallPost(`/v3/${this.props.authStore.prefix}/getLeavesAt`, {
          doctorId: this.props.doctorId,
          startDate: moment(startDate).format("YYYY-MM-DD"),
          endDate: moment(endDate).format("YYYY-MM-DD"),
          site: this.props.branchName || this.props.branchId,
          bookend: "END",
        });

        // Normalize different possible shapes
        if (resLeaves) {
          if (Array.isArray(resLeaves)) {
            resLeaves.forEach((leave) => {
              if (leave?.date) {
                leaveDates.push({
                  date: moment(leave.date).format("YYYY-MM-DD"),
                  leaveType: leave.leaveType || "Leave",
                  reason: leave.reason || "",
                  leaveData: leave,
                });
              } else if (leave?.from && leave?.to) {
                expandRangeToDates(leave.from, leave.to, leave).forEach((d) => leaveDates.push(d));
              }
            });
          } else if (resLeaves.from && resLeaves.to) {
            // API returns a single range object { from, to, originalFrom, originalTo }
            expandRangeToDates(resLeaves.from, resLeaves.to, resLeaves).forEach((d) => leaveDates.push(d));
          }
        }
      } catch (rangeError) {
        console.log("Date range API not supported, falling back to weekly range calls");

        // Fallback: query in WEEKLY RANGES (4–5 calls/month), then expand locally
        const weekRanges = [];
        let cursor = moment(startDate).startOf("day");
        const end = moment(endDate).endOf("day");
        while (cursor.isSameOrBefore(end, "day")) {
          const from = cursor.clone();
          const to = moment.min(cursor.clone().add(6, "days"), end);
          weekRanges.push({ from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") });
          cursor = to.add(1, "day");
        }

        const weekResponses = await Promise.all(
          weekRanges.map((r) =>
            apiCallPost(`/v3/${this.props.authStore.prefix}/getLeavesAt`, {
              doctorId: this.props.doctorId,
              startDate: r.from,
              endDate: r.to,
              site: this.props.branchName || this.props.branchId,
              bookend: "END",
            }).catch(() => null)
          )
        );

        weekResponses.filter(Boolean).forEach((resLeaves) => {
          if (Array.isArray(resLeaves)) {
            resLeaves.forEach((leave) => {
              if (leave?.date) {
                leaveDates.push({
                  date: moment(leave.date).format("YYYY-MM-DD"),
                  leaveType: leave.leaveType || "Leave",
                  reason: leave.reason || "",
                  leaveData: leave,
                });
              } else if (leave?.from && leave?.to) {
                expandRangeToDates(leave.from, leave.to, leave).forEach((d) => leaveDates.push(d));
              }
            });
          } else if (resLeaves?.from && resLeaves?.to) {
            expandRangeToDates(resLeaves.from, resLeaves.to, resLeaves).forEach((d) => leaveDates.push(d));
          }
        });
      }

      // De-duplicate dates
      const uniqueByDate = [];
      const seen = new Set();
      leaveDates.forEach((ld) => {
        if (ld?.date && !seen.has(ld.date)) {
          seen.add(ld.date);
          uniqueByDate.push(ld);
        }
      });

      console.log(`Fetched ${uniqueByDate.length} leave dates for the range`);
      return uniqueByDate;
    } catch (error) {
      console.error("Error loading leave dates:", error);
      cogoToast.error("Failed to load doctor leave dates");
      return [];
    }
  };

  loadClosureDates = async (startDate, endDate) => {
    if (!this.props.branchId && !this.props.branchName) {
      return [];
    }

    try {
      const res = await apiCallPost(`/v3/${this.props.authStore.prefix}/getClosuresAndHolidays`, {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        site: this.props.branchName || this.props.branchId,
      });

      const closureDates = [];

      const pushDate = (d, meta = {}) => {
        if (!d) return;
        closureDates.push({
          date: moment(d).format("YYYY-MM-DD"),
          reason: meta.reason || meta.status || "Closed",
          status: meta.status,
          raw: meta,
        });
      };

      const expand = (fromStr, toStr, meta = {}) => {
        const results = [];
        if (!fromStr || !toStr) return results;
        let cursor = moment(fromStr);
        const end = moment(toStr);
        while (cursor.isSameOrBefore(end, "day")) {
          results.push({ date: cursor.format("YYYY-MM-DD"), reason: meta.reason || meta.status || "Closed" });
          cursor = cursor.add(1, "day");
        }
        return results;
      };

      if (Array.isArray(res)) {
        res.forEach((entry) => {
          // API returns per-day entries with status like SITE_CLOSURE or OK
          if (entry?.date && String(entry?.status || "") !== "OK") {
            pushDate(entry.date, entry);
          } else if (entry?.from && entry?.to) {
            expand(entry.from, entry.to, entry).forEach((d) => closureDates.push(d));
          }
        });
      } else if (res?.from && res?.to) {
        expand(res.from, res.to, res).forEach((d) => closureDates.push(d));
      }

      // de-duplicate
      const seen = new Set();
      const uniqueByDate = closureDates.filter((cd) => {
        if (!cd?.date || seen.has(cd.date)) return false;
        seen.add(cd.date);
        return true;
      });

      return uniqueByDate;
    } catch (error) {
      console.error("Error loading closure/holiday dates:", error);
      return [];
    }
  };

  loadTimeSlots = async (date) => {
    if (!date) return;
    try {
      this.setState({ isLoadingSlots: true });

      const dateString = moment(date).format("YYYY-MM-DD");
      const scheduleData = this.state.calendarData?.scheduleMap?.[dateString] || {};
      const schedulesForDate = scheduleData.schedules || [];
      const bookedCountsForDate = this.state.bookedCountsByDate?.[dateString] || {};

      const toTimeFrame = (s) => s.timeFrame || (s.timeFrom && s.timeTo ? `${s.timeFrom} - ${s.timeTo}` : "");

      // Map schedules to time slots
      const slotsBeforeDedupe = schedulesForDate
        .map((schedule) => {
          const timeFrame = toTimeFrame(schedule);
          if (!timeFrame) return null;
          const [startTime] = timeFrame.split(" - ");
          const displayTime = startTime ? moment(startTime, "hh:mm A").format("hh:mm A") : "";
          const bookedCount = bookedCountsForDate[timeFrame] || 0;
          const capacity = schedule.surgeryCount || 0;
          const maxAllowed = capacity + 5; // Allow overbooking up to capacity + 5
          return {
            time: displayTime,
            timeFrame,
            isAvailable: capacity > 0,
            bookedCount,
            capacity,
            maxAllowed,
            isOverCapacity: bookedCount > capacity,
            isFullyBooked: bookedCount >= maxAllowed, // Only block when hitting max limit
            schedule,
          };
        })
        .filter(Boolean);

      // Deduplicate time slots by timeFrame (merge duplicates by taking max capacity)
      const slotsByTimeFrame = {};
      slotsBeforeDedupe.forEach((slot) => {
        if (!slotsByTimeFrame[slot.timeFrame]) {
          slotsByTimeFrame[slot.timeFrame] = slot;
        } else {
          // If duplicate, merge by taking the maximum capacity
          const existing = slotsByTimeFrame[slot.timeFrame];
          existing.capacity = Math.max(existing.capacity, slot.capacity);
          existing.maxAllowed = existing.capacity + 5;
          existing.isAvailable = existing.capacity > 0;
          existing.isOverCapacity = existing.bookedCount > existing.capacity;
          existing.isFullyBooked = existing.bookedCount >= existing.maxAllowed;
        }
      });

      // Convert back to array and sort by time
      const timeSlots = Object.values(slotsByTimeFrame).sort((a, b) =>
        moment(a.time, "hh:mm A").diff(moment(b.time, "hh:mm A"))
      );

      // Append an UNKNOWN bucket if there are unmatched bookings
      const UNKNOWN_TF = "__UNKNOWN__";
      const unknownCount = bookedCountsForDate[UNKNOWN_TF] || 0;
      if (unknownCount > 0) {
        timeSlots.push({
          time: "Unknown",
          timeFrame: UNKNOWN_TF,
          isAvailable: false,
          bookedCount: unknownCount,
          capacity: 0,
          maxAllowed: 0,
          isOverCapacity: false,
          isFullyBooked: false,
          schedule: null,
        });
      }

      this.setState({ timeSlots, selectedSlot: null, isLoadingSlots: false });
    } catch (error) {
      console.error("Error loading time slots:", error);
      cogoToast.error("Failed to load time slots");
      this.setState({ isLoadingSlots: false });
    }
  };

  getUniqueSchedulesByTimeFrame = (schedules = []) => {
    const uniqueSchedules = {};
    (Array.isArray(schedules) ? schedules : []).forEach((schedule) => {
      const timeFrame =
        schedule.timeFrame ||
        (schedule.timeFrom && schedule.timeTo ? `${schedule.timeFrom} - ${schedule.timeTo}` : "");
      if (!timeFrame) {
        return;
      }
      if (!uniqueSchedules[timeFrame]) {
        uniqueSchedules[timeFrame] = schedule;
        return;
      }
      const existingCapacity = Number(uniqueSchedules[timeFrame].surgeryCount) || 0;
      const newCapacity = Number(schedule.surgeryCount) || 0;
      if (newCapacity > existingCapacity) {
        uniqueSchedules[timeFrame] = schedule;
      }
    });
    return uniqueSchedules;
  };

  getDateCapacitySummary = (
    dateString,
    scheduleMap = this.state.calendarData?.scheduleMap,
    bookedCountsByDate = this.state.bookedCountsByDate
  ) => {
    const scheduleData = scheduleMap?.[dateString];
    if (!scheduleData || !Array.isArray(scheduleData.schedules)) {
      return { totalCapacity: 0, totalBooked: 0, maxAllowed: 0 };
    }

    const uniqueSchedules = this.getUniqueSchedulesByTimeFrame(scheduleData.schedules);
    const totalCapacity = Object.values(uniqueSchedules).reduce(
      (sum, schedule) => sum + (Number(schedule?.surgeryCount) || 0),
      0
    );
    const bookedCountsForDate = bookedCountsByDate?.[dateString] || {};
    const totalBooked = Object.values(bookedCountsForDate).reduce(
      (sum, n) => sum + (Number(n) || 0),
      0
    );

    return {
      totalCapacity,
      totalBooked,
      maxAllowed: totalCapacity > 0 ? totalCapacity + 5 : 0,
    };
  };

  onDateChange = async (date) => {
    if (!date) return;

    // Ignore DatePicker's synthetic onChange fired by month navigation
    if (this.state.suppressChange) {
      this.setState({ suppressChange: false });
      return;
    }

    const dateString = moment(date).format("YYYY-MM-DD");
    const scheduleData = this.state.calendarData.scheduleMap?.[dateString];
    const isAvailable = scheduleData?.available || false;
    const isLeave = scheduleData?.isLeave || false;
    const isClosure = scheduleData?.isClosure || false;
    const isPast = scheduleData?.isPast || moment(date).isBefore(moment(), "day");

    if (isPast) {
      cogoToast.error("Cannot select past dates");
      return;
    }

    if (isClosure) {
      cogoToast.error("Hospital is closed on this date");
      return;
    }

    if (isLeave) {
      cogoToast.error("Doctor is on leave on this date");
      return;
    }

    if (!isAvailable) {
      cogoToast.error("Doctor is not available on this date");
      return;
    }

    // Refresh live booked counts for the entire month, then compute slots
    const availDates =
      this.state.availableDates && this.state.availableDates.length ? this.state.availableDates : [dateString];
    const res = await this.loadBookedCountsForMonth(availDates, this.state.calendarData?.scheduleMap);
    const bookedCountsByDate = res.bookedCountsByDate || {};
    const surgeriesByDate = res.surgeriesByDate || {};
    const { totalCapacity, totalBooked, maxAllowed } = this.getDateCapacitySummary(
      dateString,
      this.state.calendarData?.scheduleMap,
      bookedCountsByDate
    );

    if (totalCapacity <= 0) {
      cogoToast.error("No available slots for this date");
      this.setState({ bookedCountsByDate, surgeriesByDate, isLoadingSlots: false });
      return;
    }

    if (maxAllowed > 0 && totalBooked >= maxAllowed) {
      cogoToast.error(`Daily booking cap reached (${totalBooked}/${maxAllowed})`);
      this.setState({ bookedCountsByDate, surgeriesByDate, isLoadingSlots: false });
      return;
    }

    this.setState(
      {
        selectedDate: date,
        isLoadingSlots: true,
        timeSlots: [],
        bookedCountsByDate,
        surgeriesByDate,
      },
      () => this.loadTimeSlots(date)
    );

    // Notify parent component
    if (this.props.onDateSelect) {
      this.props.onDateSelect(date);
    }
  };

  onSlotSelect = (slot) => {
    if (!slot.isAvailable || slot.isFullyBooked) {
      cogoToast.error("This slot is not available");
      return;
    }

    this.setState({ selectedSlot: slot });

    // Notify parent component
    if (this.props.onSlotSelect) {
      this.props.onSlotSelect(slot);
    }
  };

  onMonthChange = (date) => {
    // Dynamic month loading: When user navigates to any month (including beyond prefetched range),
    // this will trigger loadCalendarData which checks cache first, then fetches from API if needed.
    // This allows unlimited future navigation while maintaining 4-month prefetch optimization.
    this.setState(
      {
        currentMonth: date,
        selectedDate: null,
        timeSlots: [],
        suppressChange: true,
      },
      () => {
        this.debouncedLoadCalendarData();
      }
    );
  };

  // Load booked counts for a specific date
  loadBookedCountsForDate = async (date) => {
    if (!this.props.doctorId || !this.props.branchId || !date) {
      return {};
    }

    try {
      const dateString = moment(date).format("YYYY-MM-DD");

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`, {
        doctorId: this.props.doctorId,
        branchName: this.props.branchName || this.props.branchId,
        startDate: dateString,
        endDate: dateString,
        page: 1,
        limit: 100, // Should be enough for a single day
        mode: "calendar",
      });

      // Handle the API response structure properly
      const surgeriesRaw = response?.data?.items ? response.data.items : response || [];
      const surgeries = filterBookingsForSlotMetrics(surgeriesRaw);

      // Group bookings by time frame
      const bookedCounts = {};
      surgeries.forEach((booking) => {
        if (!shouldIncludeBookingInSlotMetrics(booking)) {
          return;
        }
        const timeFrame = booking.timeFrame;
        if (timeFrame) {
          bookedCounts[timeFrame] = (bookedCounts[timeFrame] || 0) + 1;
        }
      });

      console.log(`📊 Loaded ${surgeries.length} booked surgeries for date ${dateString}`);
      return bookedCounts;
    } catch (error) {
      console.error("Error loading booked counts:", error);
      return {};
    }
  };

  // Load booked counts for all dates in the current month
  loadBookedCountsForMonth = async (availableDates, scheduleMap = null) => {
    if (!this.props.doctorId || !this.props.branchId || !availableDates.length) {
      return { bookedCountsByDate: {}, surgeriesByDate: {} };
    }

    try {
      const startDate = moment(this.state.currentMonth).startOf("month").format("YYYY-MM-DD");
      const endDate = moment(this.state.currentMonth).endOf("month").format("YYYY-MM-DD");

      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getSurgeries`, {
        branchName: this.props.branchName || this.props.branchId,
        doctorId: this.props.doctorId,
        startDate: startDate,
        endDate: endDate,
        limit: 1000,
        page: 1,
        sortBy: "scheduledDate",
        sortOrder: "asc",
        mode: "calendar",
      });

      // Handle the API response structure properly
      const surgeriesRaw = response?.data?.items ? response.data.items : response || [];
      const surgeries = filterBookingsForSlotMetrics(surgeriesRaw);

      console.log("🔍 API getSurgeries response:", {
        responseStructure: response?.data ? "has data object" : "direct array",
        totalSurgeries: surgeries.length,
        apiParams: {
          doctorId: this.props.doctorId,
          branchName: this.props.branchName,
          branchId: this.props.branchId,
          startDate,
          endDate,
        },
        sampleSurgery: surgeries[0],
      });

      // Helper function to check if a time falls within a time slot
      const timeWithinSlot = (scheduledTime, slotTimeFrame) => {
        if (!scheduledTime || !slotTimeFrame) return false;

        try {
          const [slotStart, slotEnd] = slotTimeFrame.split(" - ");
          const scheduledMoment = moment(scheduledTime, ["hh:mm A", "HH:mm"]);
          const slotStartMoment = moment(slotStart, "hh:mm A");
          const slotEndMoment = moment(slotEnd, "hh:mm A");

          return scheduledMoment.isSameOrAfter(slotStartMoment) && scheduledMoment.isBefore(slotEndMoment);
        } catch (e) {
          return false;
        }
      };

      // Group bookings by date and time frame
      const bookedCountsByDate = {};
      const surgeriesByDate = {};

      surgeries.forEach((booking, index) => {
        if (!shouldIncludeBookingInSlotMetrics(booking)) {
          return;
        }
        console.log(`🔍 Surgery ${index + 1}:`, {
          patientName: booking.patientName,
          scheduledDate: booking.scheduledDate,
          scheduledDateExists: !!booking.scheduledDate,
          createdAt: booking.createdAt,
          doctorId: booking.doctorId,
          surgeonId: booking.surgeonId,
          branchId: booking.branchId,
          branchName: booking.branchName,
          timeFrame: booking.timeFrame,
          scheduledTime: booking.scheduledTime,
        });

        const date = moment(booking.scheduledDate).format("YYYY-MM-DD");

        console.log(`🔍 Surgery ${index + 1} date formatting:`, {
          originalScheduledDate: booking.scheduledDate,
          formattedDate: date,
          isValidDate: moment(booking.scheduledDate).isValid(),
        });

        if (!bookedCountsByDate[date]) {
          bookedCountsByDate[date] = {};
          surgeriesByDate[date] = [];
        }

        // Store the full surgery data for tooltip information
        surgeriesByDate[date].push(booking);

        // Use timeFrame if available for exact match, otherwise match by scheduledTime
        // Only count each surgery ONCE to avoid duplicate counts
        if (booking.timeFrame) {
          // Exact timeFrame match - preferred method
          bookedCountsByDate[date][booking.timeFrame] = (bookedCountsByDate[date][booking.timeFrame] || 0) + 1;
        } else if (booking.scheduledTime) {
          // Fallback: match surgery to the FIRST applicable time slot based on scheduledTime
          const scheduleData = scheduleMap?.[date] || this.state.calendarData?.scheduleMap?.[date];
          if (scheduleData?.schedules) {
            let matched = false;
            for (const schedule of scheduleData.schedules) {
              const slotTimeFrame =
                schedule.timeFrame ||
                (schedule.timeFrom && schedule.timeTo ? `${schedule.timeFrom} - ${schedule.timeTo}` : "");
              if (slotTimeFrame && timeWithinSlot(booking.scheduledTime, slotTimeFrame)) {
                bookedCountsByDate[date][slotTimeFrame] = (bookedCountsByDate[date][slotTimeFrame] || 0) + 1;
                console.log(`✅ Matched surgery at ${booking.scheduledTime} to slot ${slotTimeFrame}`);
                matched = true;
                break; // Only count in the first matching slot
              }
            }
            if (!matched) {
              // Count under an UNKNOWN bucket to preserve totals parity
              const UNKNOWN_TF = "__UNKNOWN__";
              bookedCountsByDate[date][UNKNOWN_TF] = (bookedCountsByDate[date][UNKNOWN_TF] || 0) + 1;
            }
          }
        }
      });

      console.log(
        `📊 Loaded ${surgeries.length} booked surgeries for month ${moment(this.state.currentMonth).format("YYYY-MM")}`
      );
      return { bookedCountsByDate, surgeriesByDate };
    } catch (error) {
      console.error("Error loading booked counts for month:", error);
      return { bookedCountsByDate: {}, surgeriesByDate: {} };
    }
  };

  // Render list of booked surgeries for the selected date
  renderBookedSurgeriesList = () => {
    if (!this.state.selectedDate || !this.props.doctorId || !this.props.branchId) {
      console.log("🔍 No selectedDate, doctorId, or branchId:", {
        selectedDate: this.state.selectedDate,
        doctorId: this.props.doctorId,
        branchId: this.props.branchId,
      });
      return null;
    }

    const dateString = moment(this.state.selectedDate).format("YYYY-MM-DD");
    const surgeriesForDate = this.state.surgeriesByDate[dateString] || [];
    const bookedCountsForDate = this.state.bookedCountsByDate[dateString] || {};
    const countedTotal = Object.values(bookedCountsForDate).reduce((sum, n) => sum + (Number(n) || 0), 0);

    // console.log("🔍 Booked surgeries debug:", {
    //  dateString,
    //  totalSurgeriesForDate: surgeriesForDate.length,
    //  surgeriesByDateKeys: Object.keys(this.state.surgeriesByDate),
    //  surgeriesByDate: this.state.surgeriesByDate,
    //  doctorId: this.props.doctorId,
    //  branchId: this.props.branchId,
    //  sampleSurgery: surgeriesForDate[0],
    // });

    // Filter surgeries for the specific doctor and branch
    // Note: API should already filter by these, but adding client-side filter as backup
    const filteredSurgeries = surgeriesForDate.filter((surgery, index) => {
      const branchMatches = surgery.branchName === this.props.branchName || surgery.branchId === this.props.branchId;
      const doctorMatches =
        surgery.doctorId === this.props.doctorId ||
        surgery.surgeonId === this.props.doctorId ||
        surgery.doctorName === this.props.doctorName;

      return branchMatches && doctorMatches;
    });

    console.log("🔍 Filtered surgeries:", filteredSurgeries.length);

    return (
      <div style={{ marginTop: "20px" }}>
        <H5
          style={{
            color: "var(--text-primary)",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          Already Booked Times ({countedTotal} counted
          {filteredSurgeries.length !== countedTotal ? `, ${filteredSurgeries.length} total` : ""})
        </H5>
        <div
          style={{
            maxHeight: "calc(100vh - 400px)",
            overflowY: "auto",
            border: "1px solid var(--border-light)",
            borderRadius: "8px",
            backgroundColor: "var(--bg-surface)",
            padding: "12px",
          }}
        >
          {filteredSurgeries.length === 0 ? (
            <Text
              style={{
                color: "var(--text-secondary)",
                textAlign: "center",
                padding: "16px",
                fontStyle: "italic",
              }}
            >
              No surgeries booked for this doctor and branch on this date
            </Text>
          ) : (
            (() => {
              // Group surgeries by time and sort
              const timeGroups = {};
              filteredSurgeries.forEach((surgery) => {
                const time = surgery.scheduledTime || surgery.timeFrame?.split(" - ")[0] || "Unknown Time";
                if (!timeGroups[time]) {
                  timeGroups[time] = [];
                }
                timeGroups[time].push(surgery);
              });

              const sortedTimes = Object.keys(timeGroups).sort((a, b) => {
                if (a === "Unknown Time") return 1;
                if (b === "Unknown Time") return -1;
                return moment(a, "hh:mm A").diff(moment(b, "hh:mm A"));
              });

              return sortedTimes.map((time, index) => {
                const surgeriesAtTime = timeGroups[time] || [];

                // Debug: log first surgery to see available fields
                if (surgeriesAtTime.length > 0) {
                  console.log("📋 [CalendarHover] surgery fields:", Object.keys(surgeriesAtTime[0]), surgeriesAtTime[0]);
                }

                // Normalise a value that may be a string, array, or comma-separated string into an array
                const toArray = (v) => {
                  if (!v) return [];
                  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
                  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
                  return [];
                };

                const extractGroups = (s) => {
                  const candidates = new Set();
                  // Primary: backend field (string or array)
                  toArray(s.surgeryGroups).forEach((g) => candidates.add(g));
                  // Fallbacks
                  toArray(s.surgeryGroup).forEach((g) => candidates.add(g));
                  toArray(s.group).forEach((g) => candidates.add(g));
                  if (Array.isArray(s.selectedSurgeries)) {
                    s.selectedSurgeries.forEach((ss) => toArray(ss.group).forEach((g) => candidates.add(g)));
                  }
                  return [...candidates].filter(Boolean);
                };

                const extractNames = (s) => {
                  // Primary: backend field (string or array)
                  const fromField = toArray(s.surgeryNames);
                  if (fromField.length > 0) return fromField;
                  // Fallbacks
                  if (Array.isArray(s.selectedSurgeries) && s.selectedSurgeries.length > 0) {
                    return s.selectedSurgeries.map((ss) => ss.name || ss.printedName).filter(Boolean);
                  }
                  if (s.printedName) return [s.printedName];
                  if (s.procedure) return [s.procedure];
                  return [];
                };

                // Build hover tooltip: groups only
                const tooltipLines = surgeriesAtTime.map((s) => {
                  const groups = extractGroups(s);
                  return groups.length > 0 ? `• ${groups.join(", ")}` : "• —";
                });
                const hoverTooltip =
                  tooltipLines.length > 0 ? tooltipLines.join("\n") : "No details";

                return (
                  <div
                    key={time}
                    title={hoverTooltip}
                    style={{
                      padding: "8px 0",
                      borderBottom: index === sortedTimes.length - 1 ? "none" : "1px solid var(--border-light)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      minHeight: "32px",
                      cursor: "default",
                    }}
                  >
                    <div>
                      <Text
                        style={{
                          fontWeight: "var(--v3-font-weight-semibold)",
                          color: "var(--text-primary)",
                          fontSize: "14px",
                        }}
                      >
                        {time} — {surgeriesAtTime.length}{" "}
                        {surgeriesAtTime.length === 1 ? "surgery" : "surgeries"}
                      </Text>
                      {/* Group chips with per-group count — disabled for now
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                        {(() => {
                          const groupCounts = {};
                          surgeriesAtTime.forEach((s) => {
                            extractGroups(s).forEach((g) => {
                              groupCounts[g] = (groupCounts[g] || 0) + 1;
                            });
                          });
                          return Object.entries(groupCounts).map(([g, count]) => (
                            <Tag key={g} minimal small intent="primary" style={{ fontSize: "11px" }}>
                              {g} {count > 1 ? count : ""}
                            </Tag>
                          ));
                        })()}
                      </div>
                      */}
                    </div>
                  </div>
                );
              });
            })()
          )}
        </div>
      </div>
    );
  };

  // Generate enhanced tooltip for calendar days
  generateDayTooltip = (isAvailable, isLeave, status, scheduleData, surgeriesForDate) => {
    if (scheduleData?.isClosure) {
      const reason = scheduleData?.closureData?.reason || scheduleData?.closureData?.status || "Closed";
      return `Closed: ${reason}`;
    }
    if (isLeave) {
      return `Leave: ${status}`;
    }

    if (!isAvailable || !scheduleData?.schedules) {
      return status;
    }

    const tooltipParts = [];

    // Add capacity information
    tooltipParts.push(`Capacity: ${status}`);

    // 1. Add slot timing information (TimeFrom - TimeTo) from schedules
    const slotTimings = scheduleData.schedules
      .map((s) => {
        if (s.timeFrom && s.timeTo) {
          return `${s.timeFrom} - ${s.timeTo}`;
        } else if (s.timeFrame) {
          return s.timeFrame;
        }
        return null;
      })
      .filter((t) => t)
      .join(", ");

    if (slotTimings) {
      tooltipParts.push(`Slots: ${slotTimings}`);
    }

    // 2. Add surgery groups from schedules (DoctorScheduleMaster)
    const surgeryGroups = scheduleData.schedules
      .map((s) => s.surgeryGroups || [])
      .flat()
      .filter((g, idx, arr) => arr.indexOf(g) === idx) // Remove duplicates
      .join(", ");

    if (surgeryGroups) {
      tooltipParts.push(`Groups: ${surgeryGroups}`);
    }

    // 3. Add scheduled times from surgeries (including duplicates as requested)
    const scheduledTimes = surgeriesForDate
      .map((s) => s.scheduledTime || s.timeFrame?.split(" - ")[0])
      .filter((t) => t)
      .join(", ");

    if (scheduledTimes) {
      tooltipParts.push(`Scheduled: ${scheduledTimes}`);
    }

    return tooltipParts.join("\n");
  };

  renderDay = (day) => {
    const dayNumber = day.getDate();
    const dateString = moment(day).format("YYYY-MM-DD");
    const isSelected = this.state.selectedDate && moment(day).isSame(this.state.selectedDate, "day");
    const isToday = moment(day).isSame(new Date(), "day");
    const isPast = moment(day).isBefore(moment(), "day");

    // Get schedule data for this date
    const scheduleData = this.state.calendarData.scheduleMap?.[dateString];
    const isAvailable = scheduleData?.available || false;
    const isLeave = scheduleData?.isLeave || false;
    const isClosure = scheduleData?.isClosure || false;
    const isPastDate = scheduleData?.isPast || isPast;

    if (
      !isPastDate &&
      Array.isArray(scheduleData?.schedules) &&
      scheduleData.schedules.length > 0 &&
      !isAvailable
    ) {
      console.log("📊 renderDay NA despite schedules", {
        dateString,
        scheduleCount: scheduleData.schedules.length,
        firstSchedule: scheduleData.schedules[0],
        isLeave,
        isClosure,
        availableFlag: scheduleData.available,
      });
    }

    // Calculate total capacity and booked slots for this date
    const { totalCapacity, totalBooked, maxAllowed } = this.getDateCapacitySummary(dateString);
    let status = "";
    let leaveInfo = "";

    if (isAvailable) {
      status = `${totalBooked}/${totalCapacity}`;
    }

    let intent = "none";
    const isOverCapacity = totalBooked > totalCapacity;

    if (isPastDate) {
      intent = "none";
      status = "Past";
    } else if (isClosure) {
      intent = "none"; // Grey out closures
      const closureData = scheduleData?.closureData;
      const reason = closureData?.reason || closureData?.status || "Closed";
      status = "CL"; // Short label for closure
    } else if (isLeave) {
      intent = "danger";
      const leaveData = scheduleData?.leaveData;
      if (leaveData?.leaveType) {
        leaveInfo = leaveData.leaveType;
        status = leaveData.leaveType;
      } else if (leaveData?.reason) {
        leaveInfo = leaveData.reason;
        status = leaveData.reason;
      } else {
        status = "Leave";
      }
    } else if (isAvailable) {
      // Check capacity status
      if (totalCapacity > 0) {
        if (isOverCapacity) {
          intent = "danger"; // Red for over capacity
          status = `${totalBooked}/${totalCapacity} (+${totalBooked - totalCapacity})`;
        } else if (totalBooked >= totalCapacity) {
          intent = "warning"; // At capacity
        } else {
          intent = "success"; // Available slots
        }
      }
      // status is already set to "x/y" format above
    } else {
      intent = "warning";
      status = "NA";
    }

    return (
      <div
        style={{
          position: "relative",
          margin: "2px",
          padding: "4px",
          borderRadius: "4px",
          backgroundColor: isSelected ? "var(--primary)" : "transparent",
          color: isSelected ? "white" : "var(--text-primary)",
          cursor:
            isAvailable && !isPastDate && totalCapacity > 0 && totalBooked < maxAllowed ? "pointer" : "not-allowed",
          opacity: isPastDate ? 0.5 : 1,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "2px" }}>{dayNumber}</div>
        <Tag
          minimal
          intent={intent}
          style={{
            fontSize: "14px",
            padding: "4px 8px",
            width: "100%",
            textAlign: "center",
            fontWeight: "600",
          }}
          title={this.generateDayTooltip(
            isAvailable,
            isLeave,
            status,
            scheduleData,
            this.state.surgeriesByDate[dateString] || []
          )}
        >
          {status}
        </Tag>
        {isToday && (
          <div
            style={{
              position: "absolute",
              top: "2px",
              right: "2px",
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: "var(--primary)",
            }}
          />
        )}
      </div>
    );
  };

  renderTimeSlots = () => {
    const { timeSlots, isLoadingSlots, selectedSlot } = this.state;

    if (isLoadingSlots) {
      return (
        <div style={{ textAlign: "center", padding: "var(--v3-spacing-lg)" }}>
          <Spinner size={40} />
          <Text style={{ marginTop: "var(--v3-spacing-md)" }}>Loading time slots...</Text>
        </div>
      );
    }

    // Use the actual time slots from the API/cache for the selected date
    // If no slots are available, show a message
    if (!timeSlots || timeSlots.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "var(--v3-spacing-lg)" }}>
          <Text className="v3-text-secondary">No time slots available for this date</Text>
        </div>
      );
    }

    // Filter slots to show only available ones (either available or fully booked)
    const availableSlots = timeSlots.filter((slot) => slot.isAvailable && !slot.isFullyBooked);
    const bookedSlots = timeSlots.filter((slot) => slot.isAvailable && slot.isFullyBooked);

    return (
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "var(--v3-spacing-sm)",
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid var(--border-light)",
            borderRadius: "var(--v3-border-radius-lg)",
            padding: "var(--v3-spacing-md) var(--v3-spacing-lg)",
            backgroundColor: "var(--bg-surface)",
          }}
        >
          {timeSlots.map((slot, index) => {
            const isSelected = selectedSlot && selectedSlot.timeFrame === slot.timeFrame;
            const isClickable = slot.isAvailable && !slot.isFullyBooked;
            const isOverCapacity = slot.isOverCapacity;

            return (
              <div
                key={slot.timeFrame || index}
                onClick={() => isClickable && this.onSlotSelect(slot)}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "var(--v3-spacing-sm) var(--v3-spacing-md)",
                  borderRadius: "var(--v3-border-radius-lg)",
                  backgroundColor: isSelected
                    ? "var(--primary)"
                    : isOverCapacity
                    ? "rgba(219, 55, 55, 0.1)" // Red background for over capacity
                    : isClickable
                    ? "var(--bg-primary)"
                    : "var(--bg-secondary)",
                  color: isSelected
                    ? "var(--text-inverse)"
                    : isOverCapacity
                    ? "#db3737" // Red text for over capacity
                    : isClickable
                    ? "var(--text-primary)"
                    : "var(--text-tertiary)",
                  cursor: isClickable ? "pointer" : "not-allowed",
                  border: isSelected
                    ? "2px solid var(--primary)"
                    : isOverCapacity
                    ? "2px solid #db3737" // Red border for over capacity
                    : isClickable
                    ? "2px solid var(--border-color)"
                    : "2px solid var(--border-light)",
                  transition: "all 0.3s ease",
                  opacity: slot.isAvailable ? 1 : 0.6,
                  boxShadow: isSelected
                    ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                    : isClickable
                    ? "0 2px 8px rgba(0, 0, 0, 0.1)"
                    : "none",
                  minHeight: "45px",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (isClickable) {
                    e.target.style.backgroundColor = isSelected ? "var(--primary)" : "var(--bg-surface-hover)";
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.2)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isClickable) {
                    e.target.style.backgroundColor = isSelected ? "var(--primary)" : "var(--bg-primary)";
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = isSelected
                      ? "0 4px 12px rgba(0, 0, 0, 0.15)"
                      : "0 2px 8px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                <Text
                  style={{
                    fontSize: "var(--v3-font-size-lg)",
                    fontWeight: "var(--v3-font-weight-semibold)",
                  }}
                >
                  {slot.timeFrame}
                </Text>
              </div>
            );
          })}
        </div>

        {/* Display booked surgeries for the selected date */}
        {this.renderBookedSurgeriesList()}

        {selectedSlot && (
          <div style={{ marginTop: "var(--v3-spacing-md)" }}>
            <FormGroup label="Chosen Time (within selected slot)">
              {(() => {
                const tf = selectedSlot.timeFrame || "";
                const [startStr, endStr] = tf.split(" - ");
                const startMoment = startStr ? moment(startStr, "hh:mm A") : null;
                const endMoment = endStr ? moment(endStr, "hh:mm A") : null;
                const valueMoment = this.props.chosenTime ? moment(this.props.chosenTime, "HH:mm") : startMoment;
                const toDate = (m) => (m ? m.toDate() : undefined);
                return (
                  <TimePicker
                    value={toDate(valueMoment)}
                    onChange={(d) => {
                      const chosenTime = moment(d).format("HH:mm");
                      if (this.props.onChosenTimeChange) {
                        this.props.onChosenTimeChange(chosenTime);
                      }
                    }}
                    useAmPm
                    showArrowButtons
                  />
                );
              })()}
            </FormGroup>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { selectedDate, isLoadingCalendar } = this.state;

    return (
      <div style={{ display: "flex", gap: "var(--v3-spacing-lg)" }}>
        {/* Calendar */}
        <Card className="v3-card" style={{ flex: 1 }}>
          <div className="v3-card-body">
            {isLoadingCalendar ? (
              <div style={{ textAlign: "center", padding: "var(--v3-spacing-lg)" }}>
                <Spinner size={40} />
                <Text style={{ marginTop: "var(--v3-spacing-md)" }}>Loading calendar...</Text>
              </div>
            ) : (
              <DatePicker
                key={moment(this.state.currentMonth).format("YYYY-MM")}
                initialMonth={this.state.currentMonth}
                value={selectedDate}
                onChange={this.onDateChange}
                onMonthChange={this.onMonthChange}
                dayPickerProps={{
                  firstDayOfWeek: 1,
                  renderDay: (day) => this.renderDay(day),
                  showOutsideDays: false,
                  onMonthChange: (d) => this.onMonthChange(d),
                }}
                minDate={new Date()}
                canClearSelection={false}
              />
            )}
          </div>
        </Card>

        {/* Time Slots */}
        {selectedDate && (
          <Card className="v3-card" style={{ flex: 1 }}>
            <div className="v3-card-header">
              <H4 className="v3-card-title">Select Time Slots - {moment(selectedDate).format("DD/MM/YYYY")}</H4>
            </div>
            <div className="v3-card-body">{this.renderTimeSlots()}</div>
          </Card>
        )}
      </div>
    );
  }
}

export default inject("authStore")(observer(SurgeryCalendar));
